import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/shared/components/ui/button';
import { Card } from '@/shared/components/ui/card';
import { apiClient } from '@/core/api/client';
import { ApiRequestError } from '@/core/api/types';
import { ExternalLink } from 'lucide-react';
import { JourneyProgress } from './JourneyProgress';
import { ErrorState } from './ErrorState';
import { UnderstandCareStage } from './UnderStandCareStage';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ResourceLinksSkeleton } from '@/features/resource-hub/components/ResourceLinksSkeleton';
import type { CareStageCardData } from './understandCareStageData';

// ─── Types ────────────────────────────────────────────────────────────────────

type ChatResponsePayload = {
  journey_acknowledgment: string;
  journey_narrative: string;
  journey_highlight_quote: string;
  journey_closing: string;
};

type JourneySummaryRequest = {
  user_type: 'care_seeker' | 'advisor';
  owner_user_id: string;
  care_stage_id: string;
  session_id: string;
  conversation: { role: string; content: string }[];
  classification: {
    is_possible: boolean;
    care_stage: string;
    care_mode: string;
    summary: string;
    zip_code: string;
    budget_range: string;
  };
};

type JourneySummaryApiResponse = Partial<ChatResponsePayload> & {
  id?: string;
  plan_name?: string;
  owner_user_id?: string;
  session_id?: string;
  care_stage_id?: string;
  data?: Partial<ChatResponsePayload> & Partial<CareContextPayload>;
  result?: Partial<ChatResponsePayload> & Partial<CareContextPayload>;
  journey_summary?: Partial<ChatResponsePayload> & Partial<CareContextPayload>;
};

type SummaryQueryResult = {
  summary: ChatResponsePayload | null;
  carePlanId: string | null;
  careContext: CareContextPayload | null;
};

type Resource = {
  id: string;
  title: string;
  seoUrl: string;
  category: string;
  type: string;
};

type ResourcesApiResponse = {
  resources?: Resource[];
};

type CareContextPayload = {
  session_id: string;
  care_stage_id: string;
};

type SituationSummaryLocationState = {
  sessionId?: string;
  careStage?: string;
};

type CardsApiResponse = {
  overviewNarrative: string | null;
  cards: CareStageCardData[];
};

// ─── Constants ────────────────────────────────────────────────────────────────

const SECTION_IDS = [
  'section-journey',
  'section-shared',
  'section-care-stage',
  'section-meet-team',
] as const;

const STICKY_OFFSET = 160; // px — navbar (~64) + JourneyProgress (~96)

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatCareStageLabel = (careStage?: string): string | null => {
  if (!careStage) return null;
  return careStage
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
};

const getCareContext = (r: JourneySummaryApiResponse): CareContextPayload | null => {
  for (const p of [r, r.data, r.result, r.journey_summary]) {
    if (p?.session_id && p.care_stage_id)
      return { session_id: p.session_id, care_stage_id: p.care_stage_id };
  }
  return null;
};

const storeCareContext = async (payload: CareContextPayload): Promise<void> => {
  const { data } = await apiClient.post<CareContextPayload>(
    '/resources/store-care-context',
    payload
  );
  sessionStorage.setItem('bquest_care_stage_id', data.care_stage_id);
};

const getNormalizedSummary = (r: JourneySummaryApiResponse): ChatResponsePayload | null => {
  const candidates = [r, r.data, r.result, r.journey_summary];
  const get = (f: keyof ChatResponsePayload) => {
    for (const p of candidates) {
      const v = p?.[f];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
    return '';
  };
  const s = {
    journey_acknowledgment: get('journey_acknowledgment'),
    journey_narrative: get('journey_narrative'),
    journey_highlight_quote: get('journey_highlight_quote'),
    journey_closing: get('journey_closing'),
  };
  return s.journey_acknowledgment || s.journey_narrative ? s : null;
};

// ─── NavItem (stepper-style with progress state) ─────────────────────────────

type NavState = 'completed' | 'active' | 'upcoming';

type NavItemProps = {
  label: string;
  state: NavState;
  onClick: () => void;
  showConnector?: boolean;
  connectorFilled?: boolean;
};

function NavItem({ label, state, onClick, showConnector = false, connectorFilled = false }: NavItemProps) {
  return (
    <div>
      <button
        type="button"
        onClick={onClick}
        className={`flex w-full items-center gap-2.5 rounded px-2 py-1.5 text-left text-sm transition-colors ${
          state === 'upcoming'
            ? 'text-gray-500 hover:text-gray-700'
            : state === 'active'
            ? 'font-semibold text-[#026857]'
            : 'text-[#026857]'
        }`}
      >
        {/* Step dot */}
        <span
          className={`relative flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all duration-300 ${
            state === 'completed'
              ? 'border-[#026857] bg-[#026857]'
              : state === 'active'
              ? 'border-[#026857] bg-white shadow-[0_0_0_3px_rgba(2,104,87,0.15)]'
              : 'border-gray-300 bg-white'
          }`}
        >
          {state === 'completed' && (
            <svg className="h-2 w-2 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 12 12">
              <polyline points="2,6 5,9 10,3" />
            </svg>
          )}
          {state === 'active' && (
            <span className="h-1.5 w-1.5 rounded-full bg-[#026857]" />
          )}
        </span>
        <span className="leading-snug">{label}</span>
      </button>

      {/* Vertical connector to next item */}
      {showConnector && (
        <div
          className={`ml-[0.6875rem] w-0.5 transition-colors duration-500 ${
            connectorFilled ? 'bg-[#026857]' : 'bg-gray-200'
          }`}
          style={{ height: '1rem' }}
        />
      )}
    </div>
  );
}

// Helper — derive nav state relative to the active section
function getSectionState(id: string, activeSection: string): NavState {
  const order = [...SECTION_IDS];
  const activeIdx = order.indexOf(activeSection as typeof SECTION_IDS[number]);
  const itemIdx = order.indexOf(id as typeof SECTION_IDS[number]);
  if (itemIdx < activeIdx) return 'completed';
  if (itemIdx === activeIdx) return 'active';
  return 'upcoming';
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SituationSummary() {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeSection, setActiveSection] = useState<string>('section-journey');
  const [passedNavCardIds, setPassedNavCardIds] = useState<Set<string>>(new Set());

  // Sub-item tooltip — rendered via portal so the sidebar's overflow-y-auto can't clip it
  const [navTooltip, setNavTooltip] = useState<{ title: string; top: number; left: number } | null>(null);

  const locationState = location.state as SituationSummaryLocationState | null;
  const routeSessionId = locationState?.sessionId;

  useEffect(() => {
    if (routeSessionId) sessionStorage.setItem('bquest_nav_session_id', routeSessionId);
  }, [routeSessionId]);
  const sessionId = routeSessionId ?? sessionStorage.getItem('bquest_nav_session_id') ?? undefined;
  const careStageLabel = formatCareStageLabel(locationState?.careStage);

  // ── Journey summary query ──────────────────────────────────────────────────
  const {
    data: queryResult,
    isPending: isLoadingSummary,
    isError: isSummaryError,
    error: summaryError,
    refetch: refetchSummary,
  } = useQuery<SummaryQueryResult>({
    queryKey: ['journey-summary', sessionId],
    enabled: !!sessionId,
    retry: false,
    staleTime: Infinity,
    queryFn: async () => {
      const cacheKey = `bquest_journey_summary_${sessionId}`;
      const cached = sessionStorage.getItem(cacheKey);

      const { data: journeyPayload } = await apiClient.get<JourneySummaryRequest>(
        '/navigator/generate-journey-summary',
        { params: { session_id: sessionId } }
      );

      if (cached) {
        const c = JSON.parse(cached) as Partial<SummaryQueryResult>;
        const id = c.careContext?.care_stage_id ?? journeyPayload.care_stage_id;
        if (id) sessionStorage.setItem('bquest_care_stage_id', id);
        return { summary: c.summary ?? null, carePlanId: c.carePlanId ?? null, careContext: c.careContext ?? null };
      }

      const { data: responseData } = await apiClient.post<JourneySummaryApiResponse>(
        '/care-engine/journey-summary',
        journeyPayload,
      );
      const careContext = getCareContext(responseData);
      if (careContext) await storeCareContext(careContext);

      const result: SummaryQueryResult = {
        summary: getNormalizedSummary(responseData),
        carePlanId: responseData.id ?? null,
        careContext,
      };
      sessionStorage.setItem(cacheKey, JSON.stringify(result));
      return result;
    },
  });

  // ── Resources query ────────────────────────────────────────────────────────
  // Independent of the journey-summary/AI call — detected_care_stage_id is already on
  // navigator_sessions from the Stage 1 chat, so this resolves directly from sessionId.
  const {
    data: resources,
    isPending: isLoadingResources,
    isError: isResourcesError,
    error: resourcesError,
  } = useQuery<Resource[]>({
    queryKey: ['summary-resources', sessionId],
    enabled: !!sessionId,
    queryFn: async () => {
      const { data } = await apiClient.get<ResourcesApiResponse>('/resources/get-all-resources', {
        params: { session_id: sessionId },
      });
      return data.resources ?? [];
    },
  });

  const situationSummary = queryResult?.summary ?? null;
  const careStageId =
    queryResult?.careContext?.care_stage_id ?? sessionStorage.getItem('bquest_care_stage_id');
  const carePlanId = queryResult?.carePlanId ?? null;

  // ── Care stage cards (shared query — React Query deduplicates the fetch) ───
  const { data: careStageData } = useQuery<CardsApiResponse>({
    queryKey: ['care-stage-consideration-cards', careStageId],
    enabled: !!careStageId,
    queryFn: async () => {
      const { data } = await apiClient.get<CardsApiResponse>(
        '/resources/care-stage-consideration-cards',
        { params: { care_stage_id: careStageId } }
      );
      return data;
    },
  });
  const sidebarCards = careStageData?.cards ?? [];

  // ── Scrollspy ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY + STICKY_OFFSET + 20;
      let current: string = SECTION_IDS[0];
      for (const id of SECTION_IDS) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= y) current = id;
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ── Care-card nav scroll tracking ─────────────────────────────────────────
  useEffect(() => {
    if (!sidebarCards.length) return;
    const CARD_OFFSET = 220;
    const handle = () => {
      const y = window.scrollY + CARD_OFFSET;
      const next = new Set<string>();
      for (const card of sidebarCards) {
        const el = document.getElementById(`care-card-${card.id}`);
        if (el && el.offsetTop <= y) next.add(card.id);
      }
      setPassedNavCardIds(next);
    };
    window.addEventListener('scroll', handle, { passive: true });
    handle();
    return () => window.removeEventListener('scroll', handle);
  }, [sidebarCards]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - STICKY_OFFSET + 8, behavior: 'smooth' });
  };

  const goToProviderOverview = () => {
    navigate('/redesign-chatbot/provider-overview', { state: { sessionId, carePlanId } });
  };

  if (isSummaryError) {
    return (
      <div className="min-h-screen bg-background">
        <JourneyProgress currentStep="summary" sessionId={sessionId} carePlanId={carePlanId} />
        <div className="px-3 py-3">
          <ErrorState
            message={(summaryError as Error)?.message || 'We could not load your journey summary right now.'}
            onRetry={() => void refetchSummary()}
            retryAfter={summaryError instanceof ApiRequestError ? summaryError.retryAfter : null}
          />
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background">
      <JourneyProgress currentStep="summary" sessionId={sessionId} carePlanId={carePlanId} />

      <div className="px-3 py-3">
        {/* Mobile/tablet shortcut — sidebar "Meet Your Team" button is hidden below lg */}
        <div className="mb-3 lg:hidden">
          <Button
            onClick={goToProviderOverview}
            size="sm"
            variant="outline"
            className="w-full border-[#026857] bg-white text-[#026857] transition-colors hover:bg-[#026857]/5 hover:text-[#026857]"
          >
            Meet Your Team
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_320px]">

          {/* ── COMBINED: Left nav + Middle content (one shared card) ──────── */}
          <div className="flex rounded-2xl border border-gray-200 bg-white shadow-sm">

            {/* Left: Planning Sections nav — sticky inside the shared card */}
            <aside className="hidden w-[260px] flex-shrink-0 border-r border-gray-100 lg:block">
              <div
                className="sticky overflow-y-auto p-5"
                style={{ top: STICKY_OFFSET, maxHeight: `calc(100vh - ${STICKY_OFFSET}px)` }}
              >
                <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Planning Sections
                </p>

                <nav>
                  {/* 1 — We Understand Your Journey */}
                  <NavItem
                    label="We Understand Your Journey"
                    state={getSectionState('section-journey', activeSection)}
                    onClick={() => scrollTo('section-journey')}
                    showConnector
                    connectorFilled={getSectionState('section-journey', activeSection) === 'completed'}
                  />

                  {/* 2 — What You've Shared */}
                  <NavItem
                    label="What You've Shared"
                    state={getSectionState('section-shared', activeSection)}
                    onClick={() => scrollTo('section-shared')}
                    showConnector
                    connectorFilled={getSectionState('section-shared', activeSection) === 'completed'}
                  />

                  {/* 3 — Understand Your Care Stage */}
                  <NavItem
                    label="Understand Your Care Stage"
                    state={getSectionState('section-care-stage', activeSection)}
                    onClick={() => scrollTo('section-care-stage')}
                  />

                  {/* Sub-items with scroll-progress fill track */}
                  {sidebarCards.length > 0 && (
                    <div className="relative my-1 ml-[0.875rem] pl-4">
                      {/* Background track */}
                      <div className="absolute left-0 top-0 bottom-0 w-0.5 overflow-hidden rounded-full bg-gray-200">
                        {/* Progressive fill — grows as each stage is scrolled past */}
                        <div
                          className="w-full rounded-full bg-[#026857] transition-all duration-500 ease-out"
                          style={{
                            height: `${
                              (sidebarCards.filter((c) => passedNavCardIds.has(c.id)).length /
                                sidebarCards.length) *
                              100
                            }%`,
                          }}
                        />
                      </div>
                      {sidebarCards.map((card, i) => {
                        const passed = passedNavCardIds.has(card.id);
                        return (
                          <div key={card.id} className="relative flex items-center gap-2 py-[3px]">
                            {/* <span
                              className={`absolute -left-[3px] z-10 h-1.5 w-1.5 rounded-full transition-all duration-300 ${
                                passed ? 'bg-[#026857]' : 'bg-gray-300'
                              }`}
                            /> */}
                            <button
                              type="button"
                              onClick={() => scrollTo(`care-card-${card.id}`)}
                              onMouseEnter={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setNavTooltip({
                                  title: `${i + 1}. ${card.title}`,
                                  top: rect.top + rect.height / 2,
                                  left: rect.right + 10,
                                });
                              }}
                              onMouseLeave={() => setNavTooltip(null)}
                              className={`truncate text-xs transition-colors hover:text-[#026857] ${
                                passed ? 'font-medium text-[#026857]' : 'text-gray-500'
                              }`}
                            >
                              {i + 1}. {card.title}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </nav>

                <div className="mt-5 space-y-2 border-t border-gray-200 pt-4">
                  <Button
                    onClick={goToProviderOverview}
                    size="sm"
                    variant="outline"
                    className="w-full border-[#026857] bg-white text-[#026857] transition-colors hover:bg-[#026857]/5 hover:text-[#026857]"
                  >
                    Meet Your Team
                  </Button>
                 
                </div>
              </div>
            </aside>

            {/* Middle: Scrollable content (shares the card with the left nav) */}
            <main className="min-w-0 flex-1 p-8">

              {/* Section 1 — Journey header */}
              <section id="section-journey" className="mb-10">
                <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-[#026857]">
                  Your Care Planning Journey
                </p>
                <h1 className="mb-3 text-3xl font-semibold text-gray-900">
                  We Understand Your Journey
                </h1>
                {careStageLabel && (
                  <div className="mb-4 inline-flex items-center rounded-full bg-[#3d7872]/10 px-4 py-1.5 text-sm font-medium text-[#2d6862]">
                    Your Care stage: {careStageLabel}
                  </div>
                )}
                <p className="text-base leading-relaxed text-gray-600">
                  {isLoadingSummary
                    ? 'Preparing your summary…'
                    : (situationSummary?.journey_acknowledgment ??
                      'We could not load your journey summary right now.')}
                </p>
              </section>

              {/* Section 2 — What You've Shared */}
              <section id="section-shared" className="mb-10">
                <h2 className="mb-4 text-xl font-semibold text-gray-900">What You've Shared</h2>
                {situationSummary ? (
                  <>
                    <p className="mb-4 leading-relaxed text-gray-700">
                      {situationSummary.journey_narrative}
                    </p>
                    <div className="my-5 rounded-xl bg-gray-50 p-5 text-center">
                      <p className="italic leading-relaxed text-gray-500">
                        "{situationSummary.journey_highlight_quote}"
                      </p>
                    </div>
                    <p className="leading-relaxed text-gray-700">
                      {situationSummary.journey_closing}
                    </p>
                  </>
                ) : (
                  <p className="leading-relaxed text-gray-700">
                    Complete the chatbot journey to see your personalized summary here.
                  </p>
                )}
              </section>

              {/* Section 3 — Understand Your Care Stage */}
              <section id="section-care-stage" className="mb-10">
                <UnderstandCareStage careStageId={careStageId} />
              </section>

              {/* Section 4 — Meet Your Care Team CTA */}
              <section id="section-meet-team">
                <Card className="border-[#3d7872]/30 bg-gradient-to-br from-[#3d7872]/10 to-[#3d7872]/5 p-6 text-center">
                  <p className="mx-auto mb-4 max-w-xl leading-relaxed text-gray-700">
                    You're not walking this path alone. We're here to help you find the right people —
                    specialists, caregivers, coordinators, and fellow travelers who understand this
                    journey.
                  </p>
                  <p className="mb-5 text-sm text-gray-600">Let's take the next step together.</p>
                  <Button
                    onClick={goToProviderOverview}
                    className="w-full rounded-xl bg-[#3d7872] px-6 py-5 text-base text-white hover:bg-[#2d6862] sm:w-auto sm:px-8 sm:py-6 sm:text-lg"
                    size="lg"
                  >
                    Meet Your Care Team
                  </Button>
                </Card>
              </section>

            </main>
          </div>

          {/* ── RIGHT: Essential Resources — its own separate card ──────────── */}
          <aside>
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm lg:sticky lg:top-[160px] lg:max-h-[calc(100vh-160px)] lg:overflow-y-auto">
              <div className="p-5">
                <h2 className="mb-1 text-base font-semibold text-gray-900">Essential Resources</h2>
                <p className="mb-4 text-xs text-gray-500">
                  Start here for expert guidance and support on your care journey.
                </p>

                <div className="space-y-3">
                  {isLoadingResources && <ResourceLinksSkeleton />}

                  {isResourcesError && (
                    <p className="text-sm text-red-600">
                      {(resourcesError as Error)?.message || 'Unable to load resources.'}
                    </p>
                  )}

                  {!isLoadingResources && !isResourcesError && resources?.length === 0 && (
                    <p className="text-sm text-gray-500">No resources available.</p>
                  )}

                  {!isLoadingResources &&
                    resources?.map((resource) => (
                      <Link
                        key={resource.id}
                        to={`/resource-hub-detail/${resource.seoUrl}`}
                        state={{ from: location.pathname }}
                        className="group block rounded-lg border border-gray-200 p-3 transition-all hover:border-[#3d7872] hover:bg-[#3d7872]/5"
                      >
                        <div className="mb-1 flex items-start justify-between gap-2">
                          <h3 className="text-xs font-semibold text-gray-900 transition-colors group-hover:text-[#3d7872]">
                            {resource.title}
                          </h3>
                          <ExternalLink
                            className="flex-shrink-0 text-gray-400 transition-colors group-hover:text-[#3d7872]"
                            size={12}
                          />
                        </div>
                        <p className="mb-1 line-clamp-1 text-xs text-gray-500">
                          {resource.category || 'Care Resource'}
                        </p>
                        <span className="text-xs font-medium text-[#3d7872]">{resource.type}</span>
                      </Link>
                    ))}
                </div>
              </div>
            </div>
          </aside>

        </div>
      </div>

      {/* Sub-item tooltip — portal to <body> so sidebar overflow can't clip it */}
      {navTooltip &&
        createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed z-[100] w-max max-w-[220px] -translate-y-1/2 whitespace-normal rounded-md bg-[#026857] px-2 py-1 text-[11px] leading-tight text-white shadow-lg"
            style={{ top: navTooltip.top, left: navTooltip.left }}
          >
            {navTooltip.title}
            <span className="absolute right-full top-1/2 -mr-px h-1.5 w-1.5 -translate-y-1/2 rotate-45 bg-[#026857]" />
          </div>,
          document.body
        )}
    </div>
  );
}
